// @ts-nocheck
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/auth/AuthContext";
import { reportIssue } from "@/api/client";
import { trackEvent } from "@/lib/posthog";
import styles from "./ReportIssueModal.module.css";

interface Props {
  open: boolean;
  onClose: () => void;
}

type IssueType = "bug" | "feature" | "question";

export default function ReportIssueModal({ open, onClose }: Props) {
  const { user } = useAuth();
  const [type, setType] = useState<IssueType>("bug");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (open) {
      document.addEventListener("keydown", handleKey);
      return () => document.removeEventListener("keydown", handleKey);
    }
  }, [open, handleKey]);

  // Reset form when opened
  useEffect(() => {
    if (open) {
      setType("bug");
      setTitle("");
      setDescription("");
      setSubmitted(false);
      setSubmitting(false);
    }
  }, [open]);

  if (!open) return null;

  async function handleSubmit() {
    if (!title.trim()) return;
    setSubmitting(true);
    try {
      await reportIssue({
        type,
        title: title.trim(),
        description: description.trim(),
        page: window.location.pathname,
        userAgent: navigator.userAgent,
      });

      trackEvent("issue_reported", { type, title: title.trim() });
      setSubmitted(true);

      // Auto-close after 2 seconds
      setTimeout(() => onClose(), 2000);
    } catch (err) {
      console.error("Failed to report issue:", err);
      setSubmitting(false);
    }
  }

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {submitted ? (
          <div className={styles.success}>
            <div className={styles.successIcon}>✓</div>
            <h3>Issue reported</h3>
            <p>We'll look into it. Thanks for letting us know.</p>
          </div>
        ) : (
          <>
            <div className={styles.header}>
              <div className={styles.iconWrap}>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>
              <div className={styles.headerText}>
                <h2>Report an issue</h2>
                <p>Describe the problem and we'll track it</p>
              </div>
            </div>

            <div className={styles.body}>
              <div className={styles.field}>
                <label>Type</label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value as IssueType)}
                >
                  <option value="bug">Bug — something's broken</option>
                  <option value="feature">Feature request</option>
                  <option value="question">Question / other</option>
                </select>
              </div>

              <div className={styles.field}>
                <label>Title</label>
                <input
                  type="text"
                  placeholder="Short summary of the issue"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  autoFocus
                />
              </div>

              <div className={styles.field}>
                <label>Description (optional)</label>
                <textarea
                  placeholder="What happened? What did you expect?"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
            </div>

            <div className={styles.actions}>
              <button
                className={styles.cancelBtn}
                onClick={onClose}
                data-attr="report-issue-cancel"
              >
                Cancel
              </button>
              <button
                className={styles.submitBtn}
                onClick={handleSubmit}
                disabled={!title.trim() || submitting}
                data-attr="report-issue-submit"
              >
                {submitting ? "Sending…" : "Submit"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
